import { useState, useEffect, useCallback } from "react";
import {
  Form,
  InputNumber,
  DatePicker,
  Select,
  Typography,
  Space,
  Tooltip,
  Switch,
  Card,
  Row,
  Col,
  Input,
  message,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import InterestCalculator from "../components/InterestCalculator";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import dayjs from "dayjs";
const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const TermsForm = ({
  onFinish,
  form,
  initialValues, // This will contain values from the contract when editing
  templateProdDetail,
  interestTypeOptions,
  defaultCurrency,
}) => {
  const [interestType, setInterestType] = useState(() => {
    // Prioritize initialValues, then templateProdDetail, then default "SI"
    if (initialValues?.interestType?.text) {
      if (initialValues.interestType.text === "Simple Interest") {
        return "SI";
      } else if (initialValues.interestType.text === "Compound Interest") {
        return "CI";
      }
    }
    if (
      templateProdDetail.interestType &&
      templateProdDetail.interestType.text
    ) {
      if (templateProdDetail.interestType.text === "Simple Interest") {
        return "SI";
      } else if (templateProdDetail.interestType.text === "Compound Interest") {
        return "CI";
      }
    }
    return "SI";
  });

  const [showTaxInput, setShowTaxInput] = useState(
    templateProdDetail.allowTax || false
  );
  const [showPlacementInput, setShowPlacementInput] = useState(
    templateProdDetail.allowPlacementFee || false
  );

  // We no longer need separate states for interestRate and tenorDays in TermsForm,
  // as we'll use useWatch to get their values directly from the form.
  // const [interestRate, setInterestRate] = useState(0);
  // const [tenorDays, setTenorDays] = useState(
  //   initialValues?.defaultTenor || templateProdDetail?.defaultTenor || 30
  // );

  const minTenor = templateProdDetail?.minTenor || 1;
  const maxTenor = templateProdDetail?.maxTenor || 365;

  const validateDefaultTenor = (_, value) => {
    if (value < minTenor) {
      return Promise.reject(`Tenor cannot be less than ${minTenor} days`);
    }
    if (value > maxTenor) {
      return Promise.reject(`Tenor cannot be greater than ${maxTenor} days`);
    }
    return Promise.resolve();
  };

  // === START: Use Ant Design's useWatch to get real-time form values ===
  const amountPlaced = Form.useWatch("amountPlaced", form);
  const interestRateValue = Form.useWatch("interestRate", form);
  const startDate = Form.useWatch("startDate", form);
  const currentTenorDays = Form.useWatch("defaultTenor", form);
  // === END: Use Ant Design's useWatch ===

  const [interestCalculationBasis, setCompoundingFrequency] = useState(() => {
    // 1. Check initialValues (contract data) first
    if (initialValues?.interestCalculationBasis) {
      const contractFreq = initialValues.interestCalculationBasis.value;
      switch (contractFreq) {
        case "annually":
        case "semiannually":
        case "quarterly":
        case "monthly":
        case "daily":
          return contractFreq;
        default:
          return "annually"; // Fallback if contract value is unexpected
      }
    }
    // 2. Fallback to templateProdDetail
    if (templateProdDetail?.interestCalculationBasis) {
      const templateFreq = templateProdDetail.interestCalculationBasis.value;
      switch (templateFreq) {
        case "annually":
        case "semiannually":
        case "quarterly":
        case "monthly":
        case "daily":
          return templateFreq;
        default:
          return "annually"; // Fallback if template value is unexpected
      }
    }
    // 3. Fallback to a hardcoded default
    return "annually";
  });

  const calculateMaturityValue = useCallback(() => {
    // These values are now directly available from useWatch
    if (
      amountPlaced === undefined ||
      amountPlaced === null || // Check for undefined/null as 0 is a valid amount
      interestRateValue === undefined ||
      interestRateValue === null ||
      !startDate ||
      currentTenorDays === undefined ||
      currentTenorDays === null
    ) {
      form.setFieldsValue({ maturityValue: 0, maturityDate: undefined }); // Clear maturityDate too
      return;
    }

    const principal = parseFloat(amountPlaced);
    const rate = parseFloat(interestRateValue) / 100;
    const tenorInYears = currentTenorDays / 365;

    let calculatedMaturityValue = 0;

    if (interestType === "SI") {
      const interest = principal * rate * tenorInYears;
      calculatedMaturityValue = principal + interest;
    } else if (interestType === "CI") {
      let n = 365;

      switch (interestCalculationBasis) {
        case 0:
          n = 365;
          break;
        case 1:
          n = 366;
          break;
        case 2:
          n = 360;
          break;
        // case "daily":
        //   n = 365;
        //   break;
        default:
          n = 365;
      }

      calculatedMaturityValue =
        principal * Math.pow(1 + rate / n, n * tenorInYears);
    }

    calculatedMaturityValue = Math.round(calculatedMaturityValue * 100) / 100;
    form.setFieldsValue({ maturityValue: calculatedMaturityValue });
  }, [
    form,
    interestType,
    interestCalculationBasis,
    amountPlaced, // Added useWatch values
    interestRateValue, // Added useWatch values
    startDate, // Added useWatch values
    currentTenorDays, // Added useWatch values
  ]);

  // Centralized useEffect to trigger calculation and maturity date update
  useEffect(() => {
    // Update maturity date whenever startDate or currentTenorDays changes
    if (
      startDate &&
      currentTenorDays !== undefined &&
      currentTenorDays !== null
    ) {
      const maturityDate = dayjs(startDate).add(currentTenorDays, "day");
      form.setFieldsValue({ maturityDate });
    } else {
      form.setFieldsValue({ maturityDate: undefined });
    }

    calculateMaturityValue();
  }, [
    form,
    interestType,
    interestCalculationBasis,
    calculateMaturityValue,
    amountPlaced,
    interestRateValue,
    startDate,
    currentTenorDays,
  ]);

  // Initial form population for edit mode
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        interestRate: initialValues.interestRate,
        defaultTenor: initialValues.defaultTenor,
        amountPlaced: initialValues.amountPlaced,
        startDate: initialValues.startDate
          ? dayjs(initialValues.startDate)
          : undefined,
      });
    }
  }, [initialValues, form]);

  const handleInterestTypeChange = (type) => {
    setInterestType(type);
  };

  return (
    <div className="form-section">
      <h2 style={{ margin: 0, fontSize: "24px" }}>Terms</h2>
      <p style={{ marginTop: "5px", color: "#6b7280" }}>
        Define Interest, Term and Placement Parameters
      </p>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          defaultTenor:
            initialValues?.defaultTenor ||
            templateProdDetail?.defaultTenor ||
            30,
          interestRate: initialValues?.interestRate,
          amountPlaced: initialValues?.amountPlaced,
          startDate: initialValues?.startDate
            ? dayjs(initialValues.startDate)
            : undefined,
          interestCalculationBasis:
            initialValues?.interestCalculationBasis?.value ||
            templateProdDetail?.interestCalculationBasis?.value ||
            "annually",
        }}
      >
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <InterestCalculator
              onInterestTypeChange={handleInterestTypeChange}
              interestType={interestType}
              interestTypeOptions={interestTypeOptions}
            />
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Rate
                  <Tooltip title="Annual interest rate percentage">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="interestRate"
            >
              <InputNumber
                style={{ width: "100%", height: "44px" }}
                placeholder="0.00"
                suffix="%"
                min={0}
                max={100}
                step={0.01}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Minimum Tenor
                  <Tooltip title="Minimum term period">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              style={{ flex: 1 }}
              name="minTenor"
            >
              <InputNumber
                style={{ width: "100%", height: "44px" }}
                placeholder="1"
                addonAfter="days"
                disabled
                className="of-bold-disable-input"
                value={minTenor}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Maximum Tenor
                  <Tooltip title="Maximum term period">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              style={{ flex: 1 }}
              name="maxTenor"
            >
              <InputNumber
                style={{ width: "100%", height: "44px" }}
                placeholder="365"
                addonAfter="days"
                disabled
                className="of-bold-disable-input"
                value={maxTenor}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Default Tenor
                  <Tooltip title="Default term period">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="defaultTenor"
              rules={[
                {
                  required: true,
                  message: "Please enter default tenor",
                },
                {
                  validator: validateDefaultTenor,
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%", height: "44px" }}
                placeholder="30"
                addonAfter="days"
                min={minTenor}
                max={maxTenor}
                // Ant Design form fields automatically update the form's internal state.
                // useWatch will pick up these changes. No need for a custom onChange here.
                // If you remove useWatch, you'd need onChange and setTenorDays state.
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Amount Placed
                  <Tooltip title="Principal amount">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="amountPlaced"
              rules={[
                {
                  required: true,
                  message: "Please enter amount",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%", height: "44px" }}
                placeholder="0.00"
                prefix={defaultCurrency}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => (value ? value.replace(/(,*)/g, "") : "")}
                min={0}
                step={0.01}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Calculation Basis
                  <Tooltip title="Basis for interest calculation">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="interestCalculationBasis"
            >
              <Input
                className="of-bold-disable-input"
                placeholder="actual360"
                style={{ height: "44px" }}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Posting
                  <Tooltip title="When interest is posted">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="interestPostingType"
            >
              <Input
                className="of-bold-disable-input"
                placeholder="At Maturity"
                style={{ height: "44px" }}
                disabled
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label="Start Date"
              name="startDate"
              style={{ flex: 1, marginBottom: 0 }}
              rules={[{ required: true, message: "Please select start date" }]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <DatePicker
                style={{ width: "100%", height: "44px", marginBottom: 0 }}
                placeholder="Select date"
                disabledDate={(current) => {
                  const startOfYear = dayjs().startOf("year");
                  return (
                    current && (current > dayjs() || current < startOfYear)
                  );
                }}
                // Ant Design form fields automatically update the form's internal state.
                // useWatch will pick up these changes. No need for a custom onChange here.
                className="bond-date-picker"
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label="Maturity Date"
              name="maturityDate"
              style={{ flex: 1 }}
            >
              <DatePicker
                style={{ marginBottom: 0, width: "100%" }}
                placeholder="DD/MM/YYYY"
                suffixIcon={<CalendarIcon color="#667085" width={20} />}
                className="bond-date-picker custom-input of-bold-disable-input disable-date"
                disabled
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Maturity Value
                  <Tooltip title="Value at maturity">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              name="maturityValue"
            >
              <InputNumber
                style={{ width: "100%", height: "44px" }}
                placeholder="0.00"
                prefix={defaultCurrency}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                disabled
                className="of-bold-disable-input"
              />
            </Form.Item>
          </Col>
          <Row>
            <Col span={24} style={{ padding: "0px 8px" }}>
              <Form.Item label="Placement Fee">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "44px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <Form.Item
                      name="allowPlacementFee"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch
                        onChange={(checked) => setShowPlacementInput(checked)}
                        disabled
                      />
                    </Form.Item>
                  </div>

                  {showPlacementInput && (
                    <Form.Item name="placementFeeCharges" noStyle>
                      <Input
                        className="of-bold-disable-input"
                        placeholder="--placement_fee--"
                        style={{ height: "44px", marginLeft: "15px" }}
                        disabled
                      />
                    </Form.Item>
                  )}
                </div>
              </Form.Item>
            </Col>

            {/* <Col span={24} style={{ padding: "0px 8px" }}>
              <Form.Item label="Tax Group">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: "44px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                    }}
                  >
                    <Form.Item name="allowTax" valuePropName="checked" noStyle>
                      <Switch
                        onChange={(checked) => setShowTaxInput(checked)}
                        disabled
                      />
                    </Form.Item>
                  </div>

                  {showTaxInput && (
                    <Form.Item name="taxGroup" noStyle>
                      <Input
                        className="of-bold-disable-input"
                        placeholder="tax_chargees"
                        style={{ height: "44px", marginLeft: "15px" }}
                        disabled
                      />
                    </Form.Item>
                  )}
                </div>
              </Form.Item>
            </Col> */}
          </Row>
        </Row>
      </Form>
    </div>
  );
};

export default TermsForm;
